import { db } from "../firebase/firebaseConfig"; // Aquí sí importas la configuración
import { collection, onSnapshot, addDoc, getDocs, query, orderBy, deleteDoc, deleteField } from "firebase/firestore";
import { doc, updateDoc } from "firebase/firestore";


// Función para actualizar un precio en la base de datos
export const updatePriceInDB = async (serviceId: string, newPrice: number) => {
  try {
    const serviceRef = doc(db, "servicios", serviceId); // Ruta en Firestore
    await updateDoc(serviceRef, { costo: newPrice }); // Actualiza el precio en Firestore
    //console.log(`Precio actualizado: ${serviceId} -> ${newPrice}`);
  } catch (error) {
    //console.error("Error actualizando precio:", error);
  }
};

// Función que escucha cambios en la base de datos en tiempo real
export const subscribeToPrecios = (callback: (data: any[]) => void) => {
  const preciosRef = collection(db, "servicios");

  return onSnapshot(preciosRef, (querySnapshot) => {
    const preciosArray = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    //console.log("Datos actualizados:", preciosArray); // Debug
    callback(preciosArray);
  });
};

export default subscribeToPrecios;

// Interfaz para las cotizaciones
export interface Quotation {
  id?: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  service: string;
  frequency: string;
  bedrooms: number;
  bathrooms: number;
  insideOven: boolean;
  insideFridge: boolean;
  insideCabinets: boolean;
  windows: boolean;
  price: number;
  wantsContact: boolean;
  createdAt: Date;
  status?: string;
}

// Función para guardar una cotización
export const saveQuotation = async (quotationData: Omit<Quotation, "id" | "createdAt">) => {
  try {
    const quotationsRef = collection(db, "cotizaciones");
    const docRef = await addDoc(quotationsRef, {
      ...quotationData,
      createdAt: new Date(),
      status: "pending"
    });
    console.log("Cotización guardada con ID:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error guardando cotización:", error);
    throw error;
  }
};

// Función para obtener todas las cotizaciones
export const getAllQuotations = async (): Promise<Quotation[]> => {
  try {
    const quotationsRef = collection(db, "cotizaciones");
    const q = query(quotationsRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Quotation[];
  } catch (error) {
    console.error("Error obteniendo cotizaciones:", error);
    throw error;
  }
};

// Función para actualizar el estado de una cotización
export const updateQuotationStatus = async (id: string, status: string): Promise<void> => {
  await updateDoc(doc(db, "cotizaciones", id), { status });
};

// Función para escuchar cambios en cotizaciones en tiempo real
export const subscribeToQuotations = (callback: (data: Quotation[]) => void) => {
  const quotationsRef = collection(db, "cotizaciones");
  const q = query(quotationsRef, orderBy("createdAt", "desc"));

  return onSnapshot(q, (querySnapshot) => {
    const quotationsArray = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Quotation[];

    callback(quotationsArray);
  });
}

// ===================================================================
// EMPLEADOS
// ===================================================================

export interface Empleado {
  id?: string;
  nombre: string;
  apellidos: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  fechaIngreso: Date;
  costoPorHora: number;
  activo: boolean;
  oculto?: boolean;
  createdAt: Date;
}

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val.toDate) return val.toDate();
  return new Date(val);
};

export const addEmpleado = async (data: Omit<Empleado, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "empleados");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToEmpleados = (callback: (data: Empleado[]) => void) => {
  const ref = collection(db, "empleados");
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaIngreso: toDate(d.data().fechaIngreso),
      createdAt: toDate(d.data().createdAt),
    })) as Empleado[]);
  });
};

export const deleteEmpleado = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "empleados", id));
};

export const updateEmpleado = async (id: string, data: Partial<Empleado>): Promise<void> => {
  await updateDoc(doc(db, "empleados", id), data as any);
};

export const ocultarEmpleado = async (id: string): Promise<void> => {
  await updateDoc(doc(db, "empleados", id), { oculto: true });
};

export const mostrarEmpleado = async (id: string): Promise<void> => {
  await updateDoc(doc(db, "empleados", id), { oculto: false });
};

// ===================================================================
// CASAS
// ===================================================================

export interface Casa {
  id?: string;
  nombreDueno: string;
  email?: string;
  direccion?: string;
  telefono?: string;
  fechaRegistro: Date;
  activa: boolean;
  createdAt: Date;
}

export const addCasa = async (data: Omit<Casa, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "casas");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToCasas = (callback: (data: Casa[]) => void) => {
  const ref = collection(db, "casas");
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaRegistro: toDate(d.data().fechaRegistro),
      createdAt: toDate(d.data().createdAt),
    })) as Casa[]);
  });
};

export const deleteCasa = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "casas", id));
};

export const updateCasa = async (id: string, data: Partial<Casa>): Promise<void> => {
  await updateDoc(doc(db, "casas", id), data as any);
};

// ===================================================================
// SERVICIOS (PAGOS)
// ===================================================================

export interface Servicio {
  id?: string;
  empleadoPrincipalId: string;
  empleadoPrincipalNombre: string;
  // Formato legado (servicios creados antes del picker)
  empleadoAyudanteId?: string;
  empleadoAyudanteNombre?: string;
  empleadoAyudante2Id?: string;
  empleadoAyudante2Nombre?: string;
  // Nuevo formato: array de ayudantes (sin límite)
  ayudantes?: { id: string; nombre: string }[];
  casaId: string;      // casa principal (backward compat)
  casaNombre: string;  // casa principal (backward compat)
  casas?: { id: string; nombre: string; direccion?: string }[]; // todas las casas del servicio
  fechaServicio: Date;
  fechaHoraInicio: Date;
  fechaHoraFin: Date;
  horasTrabajadas: number;
  precioServicio?: number;
  notas?: string;
  quincena: string; // format: "YYYY-MM-Q" e.g. "2026-04-1"
  createdAt: Date;
}

export const addServicio = async (data: Omit<Servicio, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "pagos");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToServicios = (callback: (data: Servicio[]) => void) => {
  const ref = collection(db, "pagos");
  const q = query(ref, orderBy("fechaServicio", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaServicio: toDate(d.data().fechaServicio),
      fechaHoraInicio: toDate(d.data().fechaHoraInicio),
      fechaHoraFin: toDate(d.data().fechaHoraFin),
      createdAt: toDate(d.data().createdAt),
    })) as Servicio[]);
  });
};

/** Devuelve true si el empleado participó en el servicio (soporta ambos formatos) */
export const empleadoEnServicio = (sv: Servicio, empId: string): boolean => {
  if (sv.empleadoPrincipalId === empId) return true;
  if (sv.ayudantes?.some((a) => a.id === empId)) return true;
  // legado
  if (sv.empleadoAyudanteId  === empId) return true;
  if (sv.empleadoAyudante2Id === empId) return true;
  return false;
};

/** Devuelve todas las casas del servicio (soporta ambos formatos) */
export const getCasasServicio = (sv: Servicio): { id: string; nombre: string; direccion?: string }[] => {
  if (sv.casas?.length) return sv.casas;
  return [{ id: sv.casaId, nombre: sv.casaNombre }];
};

/** Devuelve los nombres de los ayudantes (soporta ambos formatos) */
export const getAyudantesNombres = (sv: Servicio): string[] => {
  if (sv.ayudantes?.length) return sv.ayudantes.map((a) => a.nombre);
  const names: string[] = [];
  if (sv.empleadoAyudanteNombre)  names.push(sv.empleadoAyudanteNombre);
  if (sv.empleadoAyudante2Nombre) names.push(sv.empleadoAyudante2Nombre);
  return names;
};

export const deleteServicio = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "pagos", id));
};

export const updateServicio = async (id: string, data: Partial<Servicio>): Promise<void> => {
  await updateDoc(doc(db, "pagos", id), data as any);
};

/**
 * Actualiza un servicio y maneja explícitamente el borrado de campos opcionales
 * de ayudantes desde dentro de firebaseService, donde deleteField() es nativo.
 */
export const updateServicioConAyudantes = async (
  id: string,
  data: any,
  borrarAyudante1: boolean,
  borrarAyudante2: boolean
): Promise<void> => {
  const payload: any = { ...data };
  if (borrarAyudante1) {
    payload.empleadoAyudanteId     = deleteField();
    payload.empleadoAyudanteNombre = deleteField();
  }
  if (borrarAyudante2) {
    payload.empleadoAyudante2Id     = deleteField();
    payload.empleadoAyudante2Nombre = deleteField();
  }
  await updateDoc(doc(db, "pagos", id), payload);
};

// Actualizar estado de pago de un servicio (cobrado al cliente)
export const updateEstadoPagoServicio = async (
  id: string,
  estadoPago: "pagado" | "pendiente"
): Promise<void> => {
  await updateDoc(doc(db, "pagos", id), { estadoPago });
};

// ===================================================================
// SERVICIOS PERSONA NATURAL  (colección independiente: pagos_personal)
// ===================================================================

export const addServicioPersonal = async (data: Omit<Servicio, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "pagos_personal");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToServiciosPersonal = (callback: (data: Servicio[]) => void) => {
  const ref = collection(db, "pagos_personal");
  const q = query(ref, orderBy("fechaServicio", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaServicio:   toDate(d.data().fechaServicio),
      fechaHoraInicio: toDate(d.data().fechaHoraInicio),
      fechaHoraFin:    toDate(d.data().fechaHoraFin),
      createdAt:       toDate(d.data().createdAt),
    })) as Servicio[]);
  });
};

export const deleteServicioPersonal = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "pagos_personal", id));
};

export const updateServicioPersonalConAyudantes = async (
  id: string,
  data: any,
  borrarAyudante1: boolean,
  borrarAyudante2: boolean
): Promise<void> => {
  const payload: any = { ...data };
  if (borrarAyudante1) {
    payload.empleadoAyudanteId     = deleteField();
    payload.empleadoAyudanteNombre = deleteField();
  }
  if (borrarAyudante2) {
    payload.empleadoAyudante2Id     = deleteField();
    payload.empleadoAyudante2Nombre = deleteField();
  }
  await updateDoc(doc(db, "pagos_personal", id), payload);
};

// ===================================================================
// PAGOS DIRECTOS (pagos a empleados sin servicio asociado)
// ===================================================================

export interface PagoDirecto {
  id?: string;
  empleadoId: string;
  empleadoNombre: string;
  concepto: string; // Descripción del pago
  monto: number;
  fechaPago: Date;
  quincena: string; // format: "YYYY-MM-Q"
  createdAt: Date;
}

export const addPagoDirecto = async (data: Omit<PagoDirecto, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "pagos_directos");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToPagosDirectos = (callback: (data: PagoDirecto[]) => void) => {
  const ref = collection(db, "pagos_directos");
  const q = query(ref, orderBy("fechaPago", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaPago: toDate(d.data().fechaPago),
      createdAt: toDate(d.data().createdAt),
    })) as PagoDirecto[]);
  });
};

export const deletePagoDirecto = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "pagos_directos", id));
};

export const updatePagoDirecto = async (id: string, data: Partial<PagoDirecto>): Promise<void> => {
  await updateDoc(doc(db, "pagos_directos", id), data);
};

// ===================================================================
// CASAS PERSONAL  (colección independiente: casas_personal)
// ===================================================================

export const addCasaPersonal = async (data: Omit<Casa, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "casas_personal");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToCasasPersonal = (callback: (data: Casa[]) => void) => {
  const ref = collection(db, "casas_personal");
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaRegistro: toDate(d.data().fechaRegistro),
      createdAt:     toDate(d.data().createdAt),
    })) as Casa[]);
  });
};

export const deleteCasaPersonal = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, "casas_personal", id));
};

export const updateCasaPersonal = async (id: string, data: Partial<Casa>): Promise<void> => {
  await updateDoc(doc(db, "casas_personal", id), data);
};

// ===================================================================
// NÓMINA (PAGOS A EMPLEADOS)
// ===================================================================

export interface PagoNomina {
  id?: string;
  empleadoId: string;
  empleadoNombre: string;
  quincena: string;
  totalHoras?: number;
  costoPorHora?: number;
  totalPagado: number;
  serviciosIds?: string[];
  fechaPago: Date;
  createdAt: Date;
  tipo: 'servicios' | 'pagoDirecto';
  pagoDirectoId?: string;
}

export const addPagoNomina = async (data: Omit<PagoNomina, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "nomina");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToPagosNomina = (callback: (data: PagoNomina[]) => void) => {
  const ref = collection(db, "nomina");
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaPago: toDate(d.data().fechaPago),
      createdAt: toDate(d.data().createdAt),
    })) as PagoNomina[]);
  });
};

// ===================================================================
// NÓMINA PERSONAL (PAGOS A EMPLEADOS - PERSONA NATURAL)
// ===================================================================

export interface PagoNominaPersonal {
  id?: string;
  empleadoId: string;
  empleadoNombre: string;
  quincena: string;
  totalHoras: number;
  costoPorHora: number;
  totalPagado: number;
  serviciosIds: string[];
  fechaPago: Date;
  createdAt: Date;
}

export const addPagoNominaPersonal = async (data: Omit<PagoNominaPersonal, "id" | "createdAt">): Promise<string> => {
  const ref = collection(db, "nomina_personal");
  const docRef = await addDoc(ref, { ...data, createdAt: new Date() });
  return docRef.id;
};

export const subscribeToPagosNominaPersonal = (callback: (data: PagoNominaPersonal[]) => void) => {
  const ref = collection(db, "nomina_personal");
  const q = query(ref, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fechaPago: toDate(d.data().fechaPago),
      createdAt: toDate(d.data().createdAt),
    })) as PagoNominaPersonal[]);
  });
};
